export interface InitialState { state: 'initial', }
export interface LoadingState { state: 'loading', }
export interface ErroredState { state: 'errored', }
export interface CompletedState<ResultType> { state: 'completed', result: ResultType, }

export function Initial(): InitialState {
	return { state: 'initial', };
}

export function Loading(): LoadingState {
	return { state: 'loading', };
}

export function Errored(): ErroredState {
	return { state: 'errored', };
}

export function Completed<ResultType>(result: ResultType): CompletedState<ResultType> {
	return {
		state: 'completed',
		result,
	};
}

export type Result<ResultType> = InitialState | LoadingState | ErroredState | CompletedState<ResultType>;

export type CompletedType<R> = R extends CompletedState<infer ResultType> ? ResultType : never;

export function mapCompleted<OriginalResultType, MappedResultType>(result: Result<OriginalResultType>, mapper: (value: OriginalResultType) => MappedResultType) {
	if (!isComplete(result)) {
		return result;
	}

	return Completed(mapper(result.result));
}

export function isResult<ResultType = unknown>(obj: unknown): obj is Result<ResultType> {
	const { state, } = (obj as Result<ResultType>);
	return state !== undefined && (state === 'completed' || state === 'errored' || state === 'initial' || state === 'loading');
}

export function isComplete<ResultType>(result: Result<ResultType>): result is CompletedState<ResultType> {
	return result.state === 'completed';
}

export async function loadResultedData<DataType>(data: Result<DataType>, loadData: () => PromiseLike<{ result: DataType, }>, commit: (state: Result<DataType>) => void) {
	if (data.state === 'loading') {
		return;
	}

	try {
		if (data.state === 'completed') {
			const loadedData = await loadData();
			commit(Completed(loadedData.result));
			return;
		}

		// Don't display loading immediately incase it loads quick
		const loadingTimeout = setTimeout(() => commit(Loading()), 250);
		const loadedData = await loadData();
		clearTimeout(loadingTimeout);
		commit(Completed(loadedData.result));
	} catch (e) {
		console.error(e);
		commit(Errored());
	}
}
